import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Heading,
  useToast,
  VStack,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "./config/api_keys";
import NotListedAnything from "./resources/NotListedAnything";
import AnimalCardProfile from "./ProductCards/AnimalCardProfile";

export default function MyAnimals() {
  const [animals, setAnimals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState(false);
  const [deletingAnimalId, setDeletingAnimalId] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    const fetchAnimals = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_BASE_URL}/api/animals/my-animals`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setAnimals(response.data);
      } catch (error) {
        console.error("Error fetching animals:", error);
        toast({
          title: "Error",
          description: "Failed to load your animals.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAnimals();
  }, []);

  const handleDelete = async (id) => {
    setIsRemoving(true);
    setDeletingAnimalId(id);
    
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${API_BASE_URL}/api/animals/sell/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setAnimals((prevAnimals) => prevAnimals.filter((animal) => animal._id !== id));
      
      toast({
        title: "Animal Listing Deleted",
        description: "Your animal listing has been successfully deleted.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting animal:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to delete animal listing.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRemoving(false);
      setDeletingAnimalId(null);
    }
  };

  const handleEdit = (animal) => {
    // Navigate to the edit page with the animal data
    navigate(`/edit-animal/${animal._id}`, { state: { animal } });
  };

  if (animals.length === 0 && !isLoading) {
    return (
      <Box bg={bgColor} borderRadius="lg" p={6}>
        <NotListedAnything 
          title="No Animals Listed" 
          description="You haven't listed any animals yet." 
          actionText="List Your First Animal" 
          actionLink="/sell-animal" 
        />
      </Box>
    );
  }

  return (
    <Box bg={bgColor} borderRadius="lg" p={6}>
      <VStack align="stretch" spacing={6}>
        <Heading size="lg">My Animals</Heading>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <Spinner size="xl" color="green.500" />
          </Box>
        ) : (
          <>
            {animals.map((animal) => (
              <AnimalCardProfile
                key={animal._id}
                animal={animal}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
            
            {animals.length > 0 && (
              <Box textAlign="center" py={4}>
                <Button
                  colorScheme="green"
                  size="lg"
                  onClick={() => navigate("/sell-animal")}
                >
                  List Another Animal
                </Button>
              </Box>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
}